import {
  Button,
  Dialog,
  Label,
  MoneyInput,
  SelectField,
  TextField,
  parseEurInput,
} from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";

import { useCreateVehicle } from "../hooks.js";

/** The seven types, in the catalogue's declaration order — cars first, `other` last. */
const TYPES = [
  "car_petrol",
  "car_diesel",
  "car_electric",
  "bicycle",
  "ebike",
  "motorcycle",
  "other",
] as const;

/**
 * Record a vehicle.
 *
 * ==========================================================================================
 * TWO AMOUNTS, AND THEY ARE DIFFERENT KINDS OF THING (FR-050)
 * ==========================================================================================
 *
 * **What you PAID** is required and is stored on the vehicle. It is a fact about a transaction,
 * settled forever on the day of the sale, and it is what makes "this car has lost €6,100" sayable.
 *
 * **What it is worth NOW** is optional and is NOT stored on the vehicle. Supplied, it becomes an
 * ordinary `manual` valuation dated today, written in the same transaction — one path into
 * `vehicle_valuations`, so the carry-forward, the chart, the staleness prompt and the net worth band
 * all treat it as the observation it is.
 *
 * ==========================================================================================
 * THE VALUE FIELD WAS ABSENT ON PURPOSE, AND THE OBJECTION IS ANSWERED RATHER THAN DROPPED
 * ==========================================================================================
 *
 * This form used to ask for no value at all, on the argument that a value typed at creation is dated
 * the day the RECORD was made rather than the day the household knew it — and that a field here
 * makes the value feel like a property of the vehicle rather than an observation with a date, when
 * the whole module rests on the second reading.
 *
 * Both halves still hold, and neither is a reason to refuse the field:
 *
 * - The DATE is honest. Somebody adding a vehicle today and typing what they think it is worth is
 *   making an estimate today. That is exactly what the row records, and `ValuationForm` remains the
 *   place where a figure gets a date of its own.
 * - The FRAMING is carried by the words. "Roughly what is it worth now?" and the hint beneath it say
 *   this is one estimate among many rather than a property being set, which is why the label is a
 *   question and why the field is optional.
 *
 * Left out, the vehicle is simply not valued — still a legitimate state (FR-015): it contributes
 * nothing to net worth, shows no depreciation, and still groups its costs.
 *
 * ==========================================================================================
 * NO OWNERSHIP FIELD EITHER
 * ==========================================================================================
 *
 * Only outright ownership is built, so a control here would offer a choice with one option — or,
 * worse, three of which two are refused by the use case. `docs/vehicles-decisions.md` §4.1 records
 * what the other two would need.
 *
 * MOUNTED ONLY WHILE OPEN would be the other way to reset the fields; this follows
 * `RecordBalanceForm` instead and clears them on open, because a native `<dialog>` cannot be
 * conditionally rendered. Without it the second vehicle starts with the first one's values — the
 * defect 016 records for the investments dialogs.
 */
export function VehicleForm({
  open,
  onClose,
  members,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Current active members, for the owner picker. Empty means joint is the only option. */
  members: readonly { userId: string; displayName: string }[];
  onCreated?: () => void;
}): ReactNode {
  const t = useT();
  const [name, setName] = useState("");
  const [type, setType] = useState<string>("car_petrol");
  const [firstRegisteredOn, setFirstRegisteredOn] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [currentValue, setCurrentValue] = useState("");

  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName("");
      setType("car_petrol");
      setFirstRegisteredOn("");
      setOwnerId("");
      setPurchasePrice("");
      setCurrentValue("");
    }
  }

  const create = useCreateVehicle();

  const paid = parseEurInput(purchasePrice);
  /*
   * `null` covers BOTH "left empty" and "half typed", and only the second needs distinguishing from
   * a real zero — which is why the guard below tests the raw string. Zero is a legitimate purchase
   * price: a hand-me-down bicycle cost nothing, and the column's check is `>= 0` for exactly that.
   */
  const worthNow = parseEurInput(currentValue);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (name.trim() === "" || firstRegisteredOn === "" || paid === null) return;
    // A half-typed optional amount is not a reason to submit without it silently.
    if (currentValue.trim() !== "" && worthNow === null) return;

    create.mutate(
      {
        name: name.trim(),
        type,
        firstRegisteredOn,
        // An empty select means the household jointly, which is a real answer rather than a blank.
        ownerId: ownerId === "" ? null : ownerId,
        purchasePriceInEuros: paid,
        ...(worthNow === null ? {} : { currentValueInEuros: worthNow }),
      },
      {
        onSuccess: () => {
          onCreated?.();
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title={t("veh.form.heading")}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="vehicle-name">{t("veh.form.name")}</Label>
          <TextField
            id="vehicle-name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            maxLength={120}
            required
          />
          <p className="mt-1 text-xs text-muted">{t("veh.form.nameHint")}</p>
        </div>

        <div>
          <Label htmlFor="vehicle-type">{t("veh.form.type")}</Label>
          <SelectField
            id="vehicle-type"
            value={type}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              setType(event.target.value);
            }}
          >
            {TYPES.map((value) => (
              <option key={value} value={value}>
                {t(`veh.type.${value}`)}
              </option>
            ))}
          </SelectField>
        </div>

        <div>
          <Label htmlFor="vehicle-registered">{t("veh.form.firstRegistered")}</Label>
          <TextField
            id="vehicle-registered"
            type="date"
            value={firstRegisteredOn}
            onChange={(event) => {
              setFirstRegisteredOn(event.target.value);
            }}
            required
          />
          {/* It bounds every valuation date, so the form says so rather than letting a refusal say it. */}
          <p className="mt-1 text-xs text-muted">{t("veh.form.firstRegisteredHint")}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="vehicle-paid">{t("veh.form.paid")}</Label>
            <MoneyInput
              id="vehicle-paid"
              value={purchasePrice}
              onValueChange={setPurchasePrice}
              required
            />
            <p className="mt-1 text-xs text-muted">{t("veh.form.paidHint")}</p>
          </div>

          <div>
            <Label htmlFor="vehicle-worth">{t("veh.form.worthNow")}</Label>
            <MoneyInput id="vehicle-worth" value={currentValue} onValueChange={setCurrentValue} />
            {/*
              IT SAYS THE FIELD IS OPTIONAL AND SAYS WHAT HAPPENS IF IT IS FILLED. A member who types
              a figure here has recorded a dated estimate, not set a property, and the difference
              matters the first time they wonder why it did not change by itself.
            */}
            <p className="mt-1 text-xs text-muted">{t("veh.form.worthNowHint")}</p>
          </div>
        </div>

        <div>
          <Label htmlFor="vehicle-owner">{t("veh.form.owner")}</Label>
          <SelectField
            id="vehicle-owner"
            value={ownerId}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              setOwnerId(event.target.value);
            }}
          >
            <option value="">{t("veh.list.joint")}</option>
            {members.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.displayName}
              </option>
            ))}
          </SelectField>
        </div>

        {create.error ? <p className="text-sm text-danger">{errorSentence(create.error)}</p> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" pending={create.isPending}>
            {t("veh.form.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
